import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import L from 'leaflet';
// import { useAskTelegramLocation } from '../../../../lib/useAskTelegramLocation';
import { useSelector } from 'react-redux';
import { userLocation } from '../../../../store/selectors/location';
import { Button } from '../../../../components/ui/button';
import { ArrowIcon } from '../../../../assets/Arrow';
import { useTelegramBackButton } from '../../../../utils/BackButton';
import { LocationFooterIcon } from '../../../../assets/footer/Location';
import { PlusIcon } from '../../../../assets/Plus';

import { useAddressTitle } from '../../services/useAddressTitle';
import { useDeliveryAvailability } from '../../services/deliveryAvailability';
import { AddressTitleFallback } from '../component/AddressTitleFallback';
import { Skeleton } from '../../../../components/ui/skeleton';
import { useForm } from 'react-hook-form';
import { AddressSchemaForm, addresScheam } from '../../libs/addressSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '../../../../components/ui/form';
import { AdressesLabel } from '../../types/Adresses';
import { cn } from '../../../../lib/utils';

import InputField from '../../../../components/form/InputFiled';
import {
  impactOccuer,
  notificationOccurred,
} from '../../../../utils/hapticEvents';
import { editAddressData } from '../../../../store/selectors/editAddres';
import { useEditAddress } from '../../services/editAddress';
import { requestTelegramLocation } from '../../../../lib/TelegramLocation';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '../../../../components/ui/drawer';
import { Input } from '../../../../components/ui/input';
import { useAddressSearch } from '../../services/searchAddresses';
import { Card } from '../../../../components/ui/card';
import { Separator } from '../../../../components/ui/separator';
import { t } from 'i18next';
import {
  MapMoveHandler,
  PIN_SETTLE_MS,
} from '../../../../components/map/MapMoveHandler';

const INITIAL_ZOOM = 18;

export const EditAdress: React.FC = () => {
  useTelegramBackButton('/profile/addresses');
  // useAskTelegramLocation();
  const editAddress = useSelector(editAddressData);
  const { latitude: lat, longitude: lng, hasFix } = useSelector(userLocation);
  const safeLng = lng ?? 69.2401;
  const safeLat = lat ?? 41.2995;

  const locationUser: [number, number] = [safeLat, safeLng];
  const { editAddress: creatAddress, isPending } = useEditAddress(
    editAddress._id
  );

  const [hide, setHide] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const platform = window.Telegram?.WebApp?.platform;
  const isMobileTelegram = platform === 'ios' || platform === 'android';

  // `fine` — where the centre pin sits, and the only thing that is geocoded
  // and saved. The map's own position stays Leaflet's business: read on
  // moveend, written imperatively through mapRef. Keeping it in React state
  // and feeding it back into a <MapUpdater> that called setView() meant every
  // recentre emitted `move` events, which recomputed the state, which
  // recentred again — a loop that never settled.
  const mapRef = useRef<L.Map | null>(null);
  const initialCenterRef = useRef<[number, number]>([
    editAddress.location.lat,
    editAddress.location.lon,
  ]);
  const [fine, setFine] = useState<[number, number]>([
    editAddress.location.lat,
    editAddress.location.lon,
  ]);

  const { results: searchResults, isLoading: isSearching } =
    useAddressSearch(searchQuery, { lat: fine[0], lng: fine[1] });

  const {
    address,
    isLoading,
    errorMessage: addressErrorMessage,
    needsFallback: needsTitleFallback,
    manualTitle,
    setManualTitle,
    retry: retryAddressLookup,
    title: addressTitle,
    canSave,
  } = useAddressTitle(fine);

  // Вне зоны доставки адрес сохранять нечего: заказ по нему всё равно не
  // оформить. Текст отказа даёт бэкенд (см. deliveryAvailability).
  const {
    isAvailable: isDeliverable,
    message: deliveryMessage,
    isChecking: isCheckingDelivery,
  } = useDeliveryAvailability(fine);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const moveTo = (next: [number, number], zoomLevel = INITIAL_ZOOM) => {
    setFine(next);
    mapRef.current?.setView(next, zoomLevel);
  };

  const isFormFieldFocused = () => {
    const activeElement = document.activeElement;

    return (
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      activeElement instanceof HTMLSelectElement ||
      activeElement?.getAttribute('contenteditable') === 'true'
    );
  };

  // Read through a ref so both handlers stay referentially stable.
  const isInputFocusedRef = useRef(isInputFocused);
  isInputFocusedRef.current = isInputFocused;

  const isMapGestureIgnored = () =>
    isFormFieldFocused() || isInputFocusedRef.current;

  const handleMoveStart = useCallback(() => {
    if (isMapGestureIgnored()) return;
    // Fires once per gesture, not once per frame.
    setHide(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMoveEnd = useCallback((map: L.Map) => {
    if (isMapGestureIgnored()) return;

    setHide(false);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Only the geocode is debounced; the panel comes back immediately.
    debounceRef.current = setTimeout(() => {
      const center = map.getCenter();
      setFine([center.lat, center.lng]);
    }, PIN_SETTLE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const form = useForm<AddressSchemaForm>({
    resolver: zodResolver(addresScheam),
    defaultValues: {
      location: {
        lat: fine[0]! as number,
        lon: fine[1]! as number,
      },
      label_type: editAddress.label_type,
      label_value: editAddress.label_value,
      details: {
        entrance: editAddress.details.entrance,
        intercom: editAddress.details.intercom,
        floor: editAddress.details.floor,
        apartment: editAddress.details.apartment,
        comment: editAddress.details.comment,
      },
    },
  });

  const handleSubmit = async () => {
    // Кнопка выключена без названия, но submit по Enter из поля формы
    // приходит и так.
    if (!canSave || !isDeliverable) return;

    const userType =
      form.getValues('label_type') === AdressesLabel.OTHERS
        ? form.getValues('label_value')
        : form.getValues('label_type');

    await creatAddress({
      // Название — из геокодера или введённое руками (см. useAddressTitle);
      // координаты в `title` больше не подставляем.
      title: addressTitle,
      geo: `${fine[0]},${fine[1]}`,
      dom: form.getValues('details.apartment'),
      kod: form.getValues('details.intercom'),
      kv: form.getValues('details.entrance'),
      dop_nomer: form.getValues('details.floor'),
      comment: form.getValues('details.comment'),
      type: userType!,
    });
  };
  useEffect(() => {
    if (!isMobileTelegram) return;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement &&
          target.getAttribute('contenteditable') === 'true')
      ) {
        setIsInputFocused(true);
      }
    };
    const handleFocusOut = () => {
      window.setTimeout(() => {
        setIsInputFocused(isFormFieldFocused());
      }, 0);
    };

    const handleVisualViewportResize = () => {
      if (window.visualViewport) {
        const height = window.visualViewport.height;

        if (height < window.innerHeight * 0.75) {
          setIsInputFocused(true);
        } else {
          setIsInputFocused(false);
        }
      }
    };

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);
    window.visualViewport?.addEventListener(
      'resize',
      handleVisualViewportResize
    );

    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      window.visualViewport?.removeEventListener(
        'resize',
        handleVisualViewportResize
      );
    };
  }, [isMobileTelegram]);

  const isHome = form.watch('label_type') === AdressesLabel.HOME;
  const isWork = form.watch('label_type') === AdressesLabel.WORK;
  const isOther = form.watch('label_type') === AdressesLabel.OTHERS;
  const userMarkerIcon = useMemo(
    () =>
      L.icon({
        iconUrl: '/assets/pin.png',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      }),
    []
  );

  return (
    // Fixed, not absolute: vaul sets `position: fixed` on <body> while a
    // drawer is open, which would otherwise collapse this to zero height.
    <div style={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0 }}>
      <MapContainer
        ref={mapRef}
        center={initialCenterRef.current}
        zoom={INITIAL_ZOOM}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapMoveHandler
          onMoveStart={handleMoveStart}
          onMoveEnd={handleMoveEnd}
        />
        {/* Only with a real fix — the store seeds the Tashkent centre, and
            pinning "you are here" on it is worse than showing nothing. */}
        {hasFix && <Marker position={locationUser} icon={userMarkerIcon} />}
      </MapContainer>
      <div
        className={cn('absolute top-1/2 left-1/2 pointer-events-none z-10', {})}
        style={{
          transform: 'translate(-50%, -140%)',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            position: 'relative',
          }}
        >
          <img
            src="/assets/pinDrag.png"
            alt="center pin"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      </div>
      <div
        className={cn(
          'absolute bottom-0 z-20 left-0 right-0 bg-fill-background rounded-t-2xl flex flex-col p-4 transition-all duration-300',
          {
            'h-[90%]': isMobileTelegram && isInputFocused,
            'h-auto': !isMobileTelegram || !isInputFocused,
            hidden: hide,
          }
        )}
      >
        {/* Anchored to the sheet (bottom-full), not to a hardcoded offset from
            the viewport. This panel's height depends on its content — the
            label chips and the "new label" field make it taller here than on
            the add screen — so the old bottom-[360px] put the button behind
            it, and z-10 under the sheet's z-20 meant it was covered outright.
            Hidden with the keyboard up: the map isn't visible to recentre. */}
        <div
          className={cn('absolute bottom-full right-4 mb-4', {
            hidden: isInputFocused,
          })}
        >
          <Button
            className="h-12 bg-white min-w-12 rounded-full active:scale-95 transition-transform duration-150 active:animate-press-pulse text-text-icon-tertiary active:text-text-white active:bg-brand-green"
            onClick={() => {
              impactOccuer('soft');

              // Move to the known position straight away. The request below
              // is asynchronous and may put a permission dialog in front of
              // the user first; leaving the map still until it resolves is
              // what made this button read as dead.
              if (hasFix) moveTo([safeLat, safeLng]);

              // Then recentre again on the fresh fix, which is the whole
              // point of the button when the stored one is stale.
              requestTelegramLocation(({ latitude, longitude }) => {
                moveTo([latitude, longitude]);
                notificationOccurred('success');
              });
            }}
          >
            <ArrowIcon className="text-text-icon-secondary" />
          </Button>
        </div>

        <div
          className="bg-fill-base min-h-12 w-full rounded-xl py-2 px-3 flex flex-row space-x-3 items-center mb-[16px] cursor-pointer active:opacity-80 transition-opacity"
          onClick={() => {
            setOpen(true);
            setSearchQuery('');
          }}
        >
          <LocationFooterIcon className="text-text-icon-secondary w-5 h-5 shrink-0" />
          <div className="flex flex-col min-w-0 flex-1 ">
            <p className="text-caption-sm-medium text-text-tertiary">
              Город, улица, дом
            </p>
            {isLoading ? (
              <Skeleton className="w-[160px] h-3 mt-1 bg-fill-background" />
            ) : (
              // Reverse geocoding is a third-party call that does fail; an
              // empty line here read as "nothing selected".
              <p
                className={cn('text-body-xs-medium line-clamp-3 break-words', {
                  'text-text-tertiary': !address,
                })}
              >
                {address || addressErrorMessage || t('delivery-address')}
              </p>
            )}
            {/* Вне зоны доставки — прямо под адресом: это про него, а не
                про кнопку сохранения. */}
            {deliveryMessage && (
              <p className="pt-1 text-caption-sm-medium text-fill-danger">
                {deliveryMessage}
              </p>
            )}
          </div>
        </div>
        {needsTitleFallback && (
          <AddressTitleFallback
            errorMessage={addressErrorMessage}
            isRetrying={isLoading}
            value={manualTitle}
            onChange={setManualTitle}
            onRetry={() => retryAddressLookup()}
          />
        )}
        <Form {...form}>
          <div className="flex flex-col space-y-2  mb-2">
            <div className="flex flex-row space-x-1">
              <div
                className={cn(
                  'flex items-center justify-center bg-white rounded-[16px] h-7 px-3',
                  { 'border border-brand-green text-brand-green': isHome }
                )}
                onClick={() => {
                  impactOccuer('light');
                  form.setValue('label_type', AdressesLabel.HOME);
                  form.setValue('label_value', '');
                }}
              >
                <p className="text-caption-sm-medium">Дом</p>
              </div>
              <div
                className={cn(
                  'flex items-center justify-center bg-white rounded-[16px] h-7 px-3',
                  {
                    'border border-brand-green text-brand-green': isWork,
                  }
                )}
                onClick={() => {
                  impactOccuer('light');
                  form.setValue('label_type', AdressesLabel.WORK);
                  form.setValue('label_value', '');
                }}
              >
                <p className="text-caption-sm-medium">Работа</p>
              </div>
              <div
                className={cn(
                  'flex items-center justify-center bg-white rounded-[16px] h-7 px-3',
                  {
                    'border border-brand-green text-brand-green': isOther,
                  }
                )}
                onClick={() => {
                  impactOccuer('light');
                  form.setValue('label_type', AdressesLabel.OTHERS);
                }}
              >
                <PlusIcon />
              </div>
            </div>

            {isOther && (
              <InputField
                name="label_value"
                className="flex-1 bg-fill-base rounded-xl w-full h-12 border-none"
                placeholder="Новая метка"
              />
            )}
            <div className="flex flex-row gap-2 w-full">
              <div className="flex-1 min-w-0">
                <InputField
                  name="details.entrance"
                  className="w-full bg-fill-base rounded-xl border-none h-12"
                  placeholder="Подъезд"
                />
              </div>
              <div className="flex-1 min-w-0">
                <InputField
                  name="details.intercom"
                  className="w-full bg-fill-base rounded-xl border-none h-12"
                  placeholder="Домофон"
                />
              </div>
            </div>

            <div className="flex flex-row gap-2 w-full">
              <div className="flex-1 min-w-0">
                <InputField
                  name="details.floor"
                  className="flex-1 bg-fill-base rounded-xl  h-12 border-none"
                  placeholder="Этаж"
                />
              </div>
              <div className="flex-1 min-w-0">
                <InputField
                  name="details.apartment"
                  className="flex-1 bg-fill-base rounded-xl  h-12 border-none"
                  placeholder="Кв/офис"
                />
              </div>
            </div>

            <InputField
              name="details.comment"
              className="flex-1 bg-fill-base rounded-xl w-full h-12 border-none"
              placeholder="Комментарий для курьера"
            />
          </div>
          <Button
            type="submit"
            className="h-12 bg-brand-green rounded-xl"
            onClick={handleSubmit}
            disabled={
              isPending || !canSave || !isDeliverable || isCheckingDelivery
            }
          >
            <p className="text-display-sm">Сохранить</p>
          </Button>
        </Form>
      </div>
      <Drawer
        open={open}
        onClose={() => {
          setOpen(false);
          setSearchQuery('');
        }}
      >
        <DrawerContent className="h-[85%] px-4 flex flex-col space-y-2 bg-fill-background">
          <DrawerTitle>{t('delivery-address')}</DrawerTitle>
          <div className="w-full py-2">
            <Input
              className="w-full rounded-xl h-12 border-none shadow-none bg-white"
              placeholder={t('search')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              iconLeft={
                <LocationFooterIcon className="text-text-icon-secondary w-4 h-4" />
              }
            />
          </div>
          {/* Search Results */}
          <div className="flex-1 overflow-y-auto flex flex-col space-y-2">
            {isSearching && (
              <div className="flex items-center justify-center py-4">
                <Skeleton className="w-full h-12 bg-fill-base rounded-xl" />
              </div>
            )}
            {!isSearching && searchQuery && searchResults.length === 0 && (
              <div className="flex items-center justify-center py-4">
                <p className="text-body-sm-regular text-text-tertiary">
                  {t('no-results')}
                </p>
              </div>
            )}
            <Card>
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className="bg-fill-base p-3 rounded-xl cursor-pointer active:opacity-70 transition-opacity"
                  onClick={() => {
                    moveTo([result.lat, result.lng]);

                    // Close drawer
                    setOpen(false);
                    setSearchQuery('');
                  }}
                >
                  <p className="text-body-sm-semibold">{result.title}</p>

                  {result.subtitle && (
                    <p className="text-body-xs-semibold text-text-tertiary mt-1">
                      {result.subtitle}
                    </p>
                  )}
                  <Separator className="mt-2" />
                </div>
              ))}
            </Card>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default EditAdress;
